// /opt/isp_erp/frontend/src/pages/ftth/RedMapaEditor.jsx
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import {
  MapContainer, TileLayer, Marker, Popup, Polyline, useMapEvents,
} from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

import {
  Box, Card, CardContent, Chip, Stack, Typography, CircularProgress,
  Button, FormControl, InputLabel, Select, MenuItem, TextField, Alert,
  IconButton, Tooltip, Switch, FormControlLabel, Divider,
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import CenterFocusStrongIcon from '@mui/icons-material/CenterFocusStrong';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import { ftthGeoAPI } from '../../api/ftth_geo.api';
import LeafletDrawIntegration from '../../components/ftth/LeafletDrawIntegration';
import ElementoPanel from '../../components/ftth/ElementoPanel';
import { getIcon } from '../../components/ftth/ftthIcons';

const COLOR_CABLE = {
  FEEDER: '#e91e63',
  DISTRIBUCION: '#ff9800',
  DROP: '#4caf50',
};

const TIPOS_NUEVOS = [
  'POSTE', 'SPLITTER', 'SPLITTER_PRIMARIO', 'SPLITTER_SECUNDARIO',
  'NAP', 'CTO', 'CAJA_EMPALME', 'CAJA_DISTRIBUCION',
  'ODF', 'OLT', 'CAMARA', 'RESERVA',
];

// Radio de búsqueda de elementos cercanos (metros)
const RADIO_BUSQUEDA_M = 50;

// Helper: usar thumbnail si existe, si no la imagen completa
const urlImagen = (el) => el?.imagen_thumbnail || el?.imagen_url || null;

// Captura de clicks
function ClickHandler({ activo, onClickMapa }) {
  useMapEvents({
    click(e) {
      if (activo) onClickMapa(e.latlng);
    },
  });
  return null;
}

export default function RedMapaEditor() {
  const [data, setData] = useState({ elementos: [], cables: [], stats: {} });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [filtroTipo, setFiltroTipo] = useState('');
  const [busqueda, setBusqueda] = useState('');
  const [center, setCenter] = useState([-0.93, -78.61]);
  const [mapKey, setMapKey] = useState(0);

  // Modo edición
  const [modo, setModo] = useState('VER');
  const [nuevoElemento, setNuevoElemento] = useState({ tipo: 'POSTE', nombre: '' });
  const [pendingElemento, setPendingElemento] = useState(null);
  const [saving, setSaving] = useState(false);

  // Panel de propiedades
  const [selectedElemento, setSelectedElemento] = useState(null);
  const [panelOpen, setPanelOpen] = useState(false);

  // Edición de cable (arrastrar extremos)
  const [cableEditando, setCableEditando] = useState(null);
  // { cable, origenLatLng: {lat, lng}, destinoLatLng: {lat, lng} }

  // ----------------------------------------------------------
  // Cargar datos — silent=true evita remontar el mapa
  // ----------------------------------------------------------
  const cargar = useCallback(async (opts = {}) => {
    const { silent = false } = opts;
    if (!silent) setLoading(true);
    setError('');
    try {
      const d = await ftthGeoAPI.getTopologia();
      setData(d);
      // 🔑 Solo centrar la PRIMERA vez (carga inicial)
      if (!silent && d.elementos?.length && mapKey === 0) {
        setCenter([d.elementos[0].latitud, d.elementos[0].longitud]);
        setMapKey((k) => k + 1);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al cargar topología');
    } finally {
      if (!silent) setLoading(false);
    }
  }, [mapKey]);

  useEffect(() => { cargar(); }, []); // eslint-disable-line

  const elementosFiltrados = useMemo(() => {
    let list = data.elementos || [];
    if (filtroTipo) list = list.filter((e) => e.tipo === filtroTipo);
    if (busqueda.trim()) {
      const q = busqueda.toLowerCase();
      list = list.filter((e) =>
        (e.codigo || '').toLowerCase().includes(q) ||
        (e.nombre || '').toLowerCase().includes(q) ||
        (e.direccion || '').toLowerCase().includes(q)
      );
    }
    return list;
  }, [data.elementos, filtroTipo, busqueda]);

  const cablesFiltrados = useMemo(() => {
    if (!filtroTipo && !busqueda.trim()) return data.cables || [];
    const ids = new Set(elementosFiltrados.map((e) => e.id));
    return (data.cables || []).filter(
      (c) => ids.has(c.origen_id) || ids.has(c.destino_id)
    );
  }, [data.cables, filtroTipo, busqueda, elementosFiltrados]);

  const buscarElementoCercano = useCallback((latlng, radioMetros = RADIO_BUSQUEDA_M) => {
    let mejor = null;
    let mejorDist = Infinity;
    for (const e of data.elementos || []) {
      const d = Math.sqrt(
        Math.pow((e.latitud - latlng.lat) * 111320, 2) +
        Math.pow((e.longitud - latlng.lng) * 111320 * Math.cos(latlng.lat * Math.PI / 180), 2)
      );
      if (d < radioMetros && d < mejorDist) {
        mejor = e;
        mejorDist = d;
      }
    }
    return mejor;
  }, [data.elementos]);

  // ----------------------------------------------------------
  // Crear cable al dibujar
  // ----------------------------------------------------------
  const onCreated = useCallback(async (e) => {
    const { layerType, layer } = e;
    if (layerType !== 'polyline') return;

    const latlngsRaw = layer.getLatLngs()[0];
    const latlngs = latlngsRaw.map((ll) => [ll.lng, ll.lat]);

    const origen = buscarElementoCercano(latlngsRaw[0]);
    const destino = buscarElementoCercano(latlngsRaw[latlngsRaw.length - 1]);

    setSaving(true);
    try {
      await ftthGeoAPI.crearCable({
        codigo: `CABLE-${Date.now()}`,
        tipo: 'DISTRIBUCION',
        origen_id: origen ? origen.id : null,
        destino_id: destino ? destino.id : null,
        ruta_geojson: { type: 'LineString', coordinates: latlngs },
        hilos_totales: 12,
      });
      if (!origen && !destino) {
        setSuccess('Cable creado como FLOTANTE');
      } else {
        setSuccess(`Cable creado: ${origen?.codigo || '?'} → ${destino?.codigo || '?'}`);
      }
      await cargar({ silent: true });
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al guardar cable');
    } finally {
      setSaving(false);
    }
  }, [buscarElementoCercano, cargar]);

  const onClickMapa = (latlng) => {
    if (modo !== 'CREAR_ELEMENTO') return;
    setPendingElemento({ lat: latlng.lat, lng: latlng.lng });
  };

  // ----------------------------------------------------------
  // Crear elemento
  // ----------------------------------------------------------
  const confirmarCrearElemento = async () => {
    if (!pendingElemento) return;
    setSaving(true);
    try {
      const tipoPrefix = nuevoElemento.tipo.slice(0, 4);
      const codigoAuto = `${tipoPrefix}-${Date.now().toString().slice(-6)}`;

      await ftthGeoAPI.crearElemento({
        tipo: nuevoElemento.tipo,
        codigo: codigoAuto,
        nombre: nuevoElemento.nombre || codigoAuto,
        latitud: pendingElemento.lat,
        longitud: pendingElemento.lng,
      });
      setSuccess(`Elemento ${codigoAuto} creado`);
      setPendingElemento(null);
      setNuevoElemento({ tipo: nuevoElemento.tipo, nombre: '' });
      // 🔑 silent para NO mover el mapa
      await cargar({ silent: true });
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al crear elemento');
    } finally {
      setSaving(false);
    }
  };

  const abrirPanel = (elemento) => {
    setSelectedElemento(elemento);
    setPanelOpen(true);
  };

  // ----------------------------------------------------------
  // Edición de cable
  // ----------------------------------------------------------
  const activarEdicionCable = (cable) => {
    const coords = cable.ruta_geojson?.coordinates || [];
    if (coords.length < 2) return;
    setCableEditando({
      cable,
      origenLatLng: { lat: coords[0][1], lng: coords[0][0] },
      destinoLatLng: { lat: coords[coords.length - 1][1], lng: coords[coords.length - 1][0] },
    });
    setSuccess(`Editando ${cable.codigo}. Arrastra los círculos rojo/azul.`);
  };

  const guardarReconexion = async () => {
    if (!cableEditando) return;
    setSaving(true);
    setError('');
    try {
      const { cable, origenLatLng, destinoLatLng } = cableEditando;

      const origenElem = buscarElementoCercano(origenLatLng);
      const destinoElem = buscarElementoCercano(destinoLatLng);

      const coordsViejas = cable.ruta_geojson?.coordinates || [];
      const nuevasCoords = [
        [origenLatLng.lng, origenLatLng.lat],
        ...coordsViejas.slice(1, -1),
        [destinoLatLng.lng, destinoLatLng.lat],
      ];

      await ftthGeoAPI.reconectarCable(cable.id, {
        origen_id: origenElem ? origenElem.id : null,
        destino_id: destinoElem ? destinoElem.id : null,
        ruta_geojson: { type: 'LineString', coordinates: nuevasCoords },
      });

      const origenMsg = origenElem ? origenElem.codigo : 'sin anclar';
      const destinoMsg = destinoElem ? destinoElem.codigo : 'sin anclar';
      setSuccess(`Cable actualizado: ${origenMsg} → ${destinoMsg}`);
      setCableEditando(null);
      await cargar({ silent: true });
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  // ----------------------------------------------------------
  // Render
  // ----------------------------------------------------------
  return (
    <Box sx={{ position: 'relative', height: 'calc(100vh - 80px)' }}>
      <Card sx={{ position: 'absolute', zIndex: 1000, top: 10, left: 10, right: 10 }}>
        <CardContent sx={{ pb: '12px !important' }}>
          <Stack direction="row" alignItems="center" spacing={2} flexWrap="wrap">
            <Typography variant="h6" sx={{ mr: 1 }}>🎨 Red FTTH — Editor</Typography>
            <Chip size="small" color="secondary" label={`OLTs: ${data.stats?.total_olts ?? 0}`} />
            <Chip size="small" label={`Splitters: ${data.stats?.total_splitters ?? 0}`} />
            <Chip size="small" color="primary" label={`NAPs: ${data.stats?.total_naps ?? 0}`} />
            <Chip size="small" label={`Postes: ${data.stats?.total_postes ?? 0}`} />
            <Chip size="small" color="success" label={`Km: ${data.stats?.km_totales ?? 0}`} />
            <Box sx={{ flexGrow: 1 }} />
            <Button size="small" variant="outlined" startIcon={<UploadFileIcon />} href="/ftth/red/importar">
              Importar
            </Button>
            <Tooltip title="Recargar">
              <IconButton size="small" onClick={() => cargar()}><RefreshIcon /></IconButton>
            </Tooltip>
            <Tooltip title="Centrar en red">
              <IconButton size="small" onClick={() => {
                if (elementosFiltrados.length) {
                  setCenter([elementosFiltrados[0].latitud, elementosFiltrados[0].longitud]);
                  setMapKey((k) => k + 1);
                }
              }}><CenterFocusStrongIcon /></IconButton>
            </Tooltip>
          </Stack>

          <Divider sx={{ my: 1 }} />

          <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
            <FormControlLabel
              control={
                <Switch
                  checked={modo === 'DIBUJAR_CABLE'}
                  onChange={(e) => setModo(e.target.checked ? 'DIBUJAR_CABLE' : 'VER')}
                  color="primary"
                />
              }
              label="✏️ Dibujar cables"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={modo === 'CREAR_ELEMENTO'}
                  onChange={(e) => setModo(e.target.checked ? 'CREAR_ELEMENTO' : 'VER')}
                  color="secondary"
                />
              }
              label="📍 Crear elementos"
            />

            {modo === 'CREAR_ELEMENTO' && (
              <>
                <FormControl size="small" sx={{ minWidth: 200 }}>
                  <InputLabel>Tipo</InputLabel>
                  <Select
                    value={nuevoElemento.tipo}
                    label="Tipo"
                    onChange={(e) => setNuevoElemento({ ...nuevoElemento, tipo: e.target.value })}
                  >
                    {TIPOS_NUEVOS.map((t) => (
                      <MenuItem key={t} value={t}>{t}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField
                  size="small"
                  label="Nombre (opcional)"
                  value={nuevoElemento.nombre}
                  onChange={(e) => setNuevoElemento({ ...nuevoElemento, nombre: e.target.value })}
                  sx={{ minWidth: 180 }}
                />
                <Typography variant="caption" color="secondary">
                  Haz clic en el mapa
                </Typography>
              </>
            )}

            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Tipo</InputLabel>
              <Select value={filtroTipo} label="Tipo" onChange={(e) => setFiltroTipo(e.target.value)}>
                <MenuItem value="">Todos</MenuItem>
                {TIPOS_NUEVOS.map((t) => (
                  <MenuItem key={t} value={t}>{t}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              size="small"
              label="Buscar"
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              sx={{ flexGrow: 1, minWidth: 180 }}
            />
          </Stack>

          {error && <Alert severity="error" sx={{ mt: 1 }} onClose={() => setError('')}>{error}</Alert>}
          {success && <Alert severity="success" sx={{ mt: 1 }} onClose={() => setSuccess('')}>{success}</Alert>}
        </CardContent>
      </Card>

      {loading ? (
        <Box display="flex" alignItems="center" justifyContent="center" height="100%">
          <CircularProgress />
        </Box>
      ) : (
        <MapContainer
          key={mapKey}
          center={center}
          zoom={15}
          style={{ height: '100%', width: '100%' }}
        >
          <TileLayer
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            attribution='&copy; OpenStreetMap contributors'
          />

          <ClickHandler activo={modo === 'CREAR_ELEMENTO'} onClickMapa={onClickMapa} />

          <LeafletDrawIntegration
            activo={modo === 'DIBUJAR_CABLE'}
            onCreated={onCreated}
            mode="polyline"
          />

          {/* Cables */}
          {cablesFiltrados.map((c) => {
            const coords = c.ruta_geojson?.coordinates || [];
            if (coords.length < 2) return null;
            const positions = coords.map(([lng, lat]) => [lat, lng]);
            const editando = cableEditando?.cable?.id === c.id;

            return (
              <Polyline
                key={`c-${c.id}`}
                positions={positions}
                color={editando ? '#1976d2' : (COLOR_CABLE[c.tipo] || '#555')}
                weight={editando ? 4 : 2}
                dashArray={editando ? '8 4' : undefined}
                eventHandlers={{
                  dblclick: () => activarEdicionCable(c),
                }}
              >
                <Popup>
                  <b>Cable {c.tipo}</b><br />
                  {c.codigo}<br />
                  {Math.round(c.longitud_m)} m
                  {(!c.origen_id || !c.destino_id) && <><br /><i>Flotante</i></>}
                  <br />
                  <small>Doble clic para editar extremos</small>
                </Popup>
              </Polyline>
            );
          })}

          {/* Marcadores arrastrables para editar extremos del cable */}
          {cableEditando && (
            <>
              <Marker
                position={[cableEditando.origenLatLng.lat, cableEditando.origenLatLng.lng]}
                draggable
                icon={L.divIcon({
                  className: '',
                  html: '<div style="background:#d32f2f;width:18px;height:18px;border-radius:50%;border:3px solid white;box-shadow:0 0 6px rgba(0,0,0,.7);cursor:move"></div>',
                  iconSize: [18, 18],
                  iconAnchor: [9, 9],
                })}
                eventHandlers={{
                  dragend: (e) => {
                    const { lat, lng } = e.target.getLatLng();
                    setCableEditando((prev) => ({
                      ...prev,
                      origenLatLng: { lat, lng },
                    }));
                  },
                }}
              />
              <Marker
                position={[cableEditando.destinoLatLng.lat, cableEditando.destinoLatLng.lng]}
                draggable
                icon={L.divIcon({
                  className: '',
                  html: '<div style="background:#1976d2;width:18px;height:18px;border-radius:50%;border:3px solid white;box-shadow:0 0 6px rgba(0,0,0,.7);cursor:move"></div>',
                  iconSize: [18, 18],
                  iconAnchor: [9, 9],
                })}
                eventHandlers={{
                  dragend: (e) => {
                    const { lat, lng } = e.target.getLatLng();
                    setCableEditando((prev) => ({
                      ...prev,
                      destinoLatLng: { lat, lng },
                    }));
                  },
                }}
              />
            </>
          )}

          {/* Elementos (clickeables) */}
          {elementosFiltrados.map((el) => {
            const img = urlImagen(el);
            return (
              <Marker
                key={`e-${el.id}`}
                position={[el.latitud, el.longitud]}
                icon={getIcon(el.tipo)}
                eventHandlers={{
                  click: () => abrirPanel(el),
                }}
              >
                <Popup>
                  {/* Imagen (usa thumbnail si existe) */}
                  {img && (
                    <img
                      src={img}
                      alt={el.codigo}
                      style={{
                        width: 200,
                        height: 120,
                        objectFit: 'cover',
                        borderRadius: 4,
                        marginBottom: 8,
                        display: 'block',
                      }}
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  )}
                  <b>{el.tipo}</b><br />
                  <b>{el.codigo}</b><br />
                  {el.nombre && <>{el.nombre}<br /></>}
                  {el.direccion && <>{el.direccion}<br /></>}
                  <Button size="small" onClick={() => abrirPanel(el)}>
                    Editar / Ver detalles
                  </Button>
                </Popup>
              </Marker>
            );
          })}

          {pendingElemento && (
            <Marker position={[pendingElemento.lat, pendingElemento.lng]} icon={getIcon('OTRO')} />
          )}
        </MapContainer>
      )}

      {/* Barra flotante para editar cable */}
      {cableEditando && (
        <Card
          sx={{
            position: 'absolute',
            bottom: 20,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 1100,
            minWidth: 460,
          }}
        >
          <CardContent sx={{ py: '12px !important' }}>
            <Stack direction="row" alignItems="center" spacing={2}>
              <Typography variant="body2">
                Editando: <b>{cableEditando.cable.codigo}</b>
              </Typography>
              <Box sx={{ flexGrow: 1 }} />
              <Button
                size="small"
                variant="outlined"
                onClick={() => setCableEditando(null)}
                disabled={saving}
              >
                Cancelar
              </Button>
              <Button
                size="small"
                variant="contained"
                color="primary"
                onClick={guardarReconexion}
                disabled={saving}
              >
                {saving ? 'Guardando...' : 'Guardar cambios'}
              </Button>
            </Stack>
          </CardContent>
        </Card>
      )}

      {/* Panel lateral de propiedades */}
      <ElementoPanel
        elemento={selectedElemento}
        open={panelOpen}
        onClose={() => setPanelOpen(false)}
        onUpdated={(el) => {
          setSelectedElemento(el);
          cargar({ silent: true });
        }}
        onDeleted={() => {
          setSelectedElemento(null);
          cargar({ silent: true });
        }}
      />

      {/* Crear elemento */}
      <Dialog open={!!pendingElemento} onClose={() => setPendingElemento(null)}>
        <DialogTitle>Crear elemento</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Se creará un <b>{nuevoElemento.tipo}</b> en
            <br />
            lat: {pendingElemento?.lat?.toFixed(6)} / lng: {pendingElemento?.lng?.toFixed(6)}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingElemento(null)} startIcon={<CancelIcon />} disabled={saving}>
            Cancelar
          </Button>
          <Button variant="contained" onClick={confirmarCrearElemento} startIcon={<SaveIcon />} disabled={saving}>
            {saving ? 'Creando...' : 'Crear'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
